<template>
    <div>
        <b-modal id="modal-apply-absence"
                 ref="md-this"
                 title="Record Absence & Time Off"
                 hide-footer
                 @shown="onModelShown"
                 size="xl"
                 dialog-class="modal-dialog-apply-absence"
                 data-test="modal-apply-absence">
            <b-overlay :show="loading">
                <ValidationObserver v-slot="{ handleSubmit }">
                    <b-form @submit.prevent="handleSubmit(applyAbsence)">
                        <!-- Absence Type Picker -->
                        <b-row class="my-1 justify-content-center no-gutters">
                            <b-col cols="11" sm="8" md="6" lg="5" class="text-center px-2">
                                <b-form-group label="Select Absence Type" label-for="absType" class="mb-1" label-class="font-weight-600 mb-1">
                                    <b-form-select id="absType" name="type" v-model="pickedAbsenceType" required data-test="select-absence-type">
                                        <b-form-select-option v-for="at in absenceTypes" :key="at" :value="at">{{ at }}</b-form-select-option>
                                    </b-form-select>
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <div v-if="pickedAbsenceType">
                            <!-- Helper Text & Legend -->
                            <b-row class="mt-2 mb-1 no-gutters">
                                <b-col class="text-center">
                                    <small class="text-muted d-block mb-1">
                                        <span v-if="!fromDate">Click a date on the calendar to set <strong>From</strong></span>
                                        <span v-else-if="fromDate && !toDate">Click an end date to set <strong>To</strong> (or click the same date for 1 day)</span>
                                        <span v-else>Range selected. Click any date to start a new selection.</span>
                                    </small>
                                </b-col>
                            </b-row>

                            <!-- Month Navigation Toolbar -->
                            <div class="d-flex justify-content-center align-items-center my-2 calendar-nav-bar flex-wrap">
                                <b-button-group size="sm" class="mr-1 mr-md-2 my-1">
                                    <b-button variant="outline-secondary"
                                              @click="navigateMonths(-3)"
                                              :disabled="isPrevNavDisabled(-3)"
                                              title="Move back 3 months"
                                              data-test="btn-absence-prev-3m">
                                        &laquo; 3 Months
                                    </b-button>
                                    <b-button variant="outline-secondary"
                                              @click="navigateMonths(-1)"
                                              :disabled="isPrevNavDisabled(-1)"
                                              title="Move back 1 month"
                                              class="calendar-nav-step-btn"
                                              data-test="btn-absence-prev-1m">
                                        &lsaquo;
                                    </b-button>
                                </b-button-group>

                                <b-button variant="outline-primary"
                                          size="sm"
                                          class="mx-1 my-1 calendar-nav-today-btn"
                                          @click="navigateToday"
                                          :disabled="isCurrentMonthDisplayed"
                                          title="Jump to current month"
                                          data-test="btn-absence-today">
                                    Today
                                </b-button>

                                <b-button-group size="sm" class="ml-1 ml-md-2 my-1">
                                    <b-button variant="outline-secondary"
                                              @click="navigateMonths(1)"
                                              title="Move forward 1 month"
                                              class="calendar-nav-step-btn"
                                              data-test="btn-absence-next-1m">
                                        &rsaquo;
                                    </b-button>
                                    <b-button variant="outline-secondary"
                                              @click="navigateMonths(3)"
                                              title="Move forward 3 months"
                                              data-test="btn-absence-next-3m">
                                        3 Months &raquo;
                                    </b-button>
                                </b-button-group>
                            </div>

                            <!-- Multi-Month Interactive Range Calendars -->
                            <div class="d-flex justify-content-center flex-wrap calendar-container-row my-2">
                                <div class="calendar-wrapper mb-3" v-if="isPrevMonthVisible">
                                    <b-calendar ref="prevCal"
                                                :key="prevMonthMin + '_' + calendarKey"
                                                class="absence-modal-calendar"
                                                :block="true"
                                                :start-weekday="1"
                                                locale="en"
                                                hide-header
                                                :min="prevMonthMin"
                                                :max="prevMonthMax"
                                                :initial-date="prevMonthInitial"
                                                :date-info-fn="statusStyle"
                                                :date-disabled-fn="calendarDisabledDates"
                                                @selected="onCalendarSelected">
                                    </b-calendar>
                                </div>
                                <div class="calendar-wrapper mb-3">
                                    <b-calendar ref="currCal"
                                                :key="currentMonthMin + '_' + calendarKey"
                                                class="absence-modal-calendar"
                                                :block="true"
                                                :start-weekday="1"
                                                locale="en"
                                                hide-header
                                                :min="currentMonthMin"
                                                :max="currentMonthMax"
                                                :initial-date="currentMonthInitial"
                                                :date-info-fn="statusStyle"
                                                :date-disabled-fn="calendarDisabledDates"
                                                @selected="onCalendarSelected">
                                    </b-calendar>
                                </div>
                                <div class="calendar-wrapper mb-3">
                                    <b-calendar ref="nextCal"
                                                :key="nextMonthMin + '_' + calendarKey"
                                                class="absence-modal-calendar"
                                                :block="true"
                                                :start-weekday="1"
                                                locale="en"
                                                hide-header
                                                :min="nextMonthMin"
                                                :max="nextMonthMax"
                                                :initial-date="nextMonthInitial"
                                                :date-info-fn="statusStyle"
                                                :date-disabled-fn="calendarDisabledDates"
                                                @selected="onCalendarSelected">
                                    </b-calendar>
                                </div>
                            </div>
                            <div class="calendar-legend">
                                <span class="legend-item"><span class="legend-dot legend-eligible"></span> Eligible Absence</span>
                                <span class="legend-item"><span class="legend-dot legend-booked"></span> Already Logged</span>
                                <span class="legend-item"><span class="legend-dot legend-holiday"></span> Public Holiday</span>
                                <span class="legend-item"><span class="legend-dot legend-weekend calendar-legend-weekend"></span> Weekend / Non-working</span>
                            </div>

                            <!-- Total Summary -->
                            <b-row class="my-2 no-gutters" v-if="fromDate">
                                <b-col class="text-center">
                                    <template v-if="fromDate && toDate">
                                        <h4 v-if="totalDays > 0" class="text-info font-weight-bold mb-1">
                                            Total {{ totalDays }} working {{ totalDays === 1 ? 'day' : 'days' }}
                                            <small class="d-block text-muted font-weight-normal mt-1" style="font-size: 0.85rem;">
                                                ({{ fromDate === toDate ? fromDate : fromDate + ' to ' + toDate }})
                                            </small>
                                        </h4>
                                        <b-alert v-else show variant="warning" class="mb-0 py-2 small">
                                            No eligible working days in this range ({{ fromDate === toDate ? fromDate : fromDate + ' to ' + toDate }}). Weekends, holidays, and already logged dates are skipped.
                                        </b-alert>
                                    </template>
                                    <h5 v-else class="text-muted font-weight-bold mb-0">
                                        From: {{ fromDate }} &rarr; <em>(click an end date)</em>
                                    </h5>
                                </b-col>
                            </b-row>

                            <!-- Action Buttons -->
                            <b-row class="my-3 justify-content-center no-gutters" align-h="center">
                                <b-col v-if="canConfirmApplyAbsence()" cols="auto" class="text-center px-1">
                                    <b-button type="submit" variant="info" :disabled="totalDays <= 0" class="absence-action-btn" data-test="btn-submit-absence">
                                        {{ !isCorrection ? 'Apply' : 'Correct' }}
                                    </b-button>
                                </b-col>
                                <b-col cols="auto" class="text-center px-1">
                                    <b-button @click="resetDateRange" variant="outline-secondary" :disabled="!fromDate && !toDate" class="absence-action-btn" data-test="btn-clear-absence">
                                        Clear
                                    </b-button>
                                </b-col>
                                <b-col v-if="canShowRemoveButton()" cols="auto" class="text-center px-1">
                                    <b-button @click="removeAbsence" variant="danger" class="absence-action-btn" data-test="btn-remove-absence">
                                        Remove
                                    </b-button>
                                </b-col>
                                <b-col cols="auto" class="text-center px-1">
                                    <b-button @click="closeWindow" variant="secondary" class="absence-action-btn" data-test="btn-cancel-absence">
                                        Cancel
                                    </b-button>
                                </b-col>
                            </b-row>
                        </div>
                    </b-form>
                </ValidationObserver>
                <template #overlay>
                    <load-spinner text="Saving absence..." />
                </template>
            </b-overlay>
        </b-modal>
    </div>
</template>

<script>
    import sharedMixin from '../mixinAccount';
    import toastMixin from '../mixinToast';
    import { format } from 'date-fns';

    export default {
        mixins: [sharedMixin, toastMixin],
        data() {
            return {
                modal: null,
                absenceTypes: [],
                pickedAbsenceType: "Vacation",
                fromDate: "",
                toDate: "",
                loadedPickSignature: {},
                totalDays: 0,
                loading: false,
                onAppliedChangesAsync: null,
                isCorrection: false,
                correctionReferenceUid: null,
                currentAbsenceDatesMap: {},
                publicHolidays: {},
                bookedDaysMap: {},
                prevMonthMin: null,
                prevMonthMax: null,
                prevMonthInitial: null,
                currentMonthMin: null,
                currentMonthMax: null,
                currentMonthInitial: null,
                nextMonthMin: null,
                nextMonthMax: null,
                nextMonthInitial: null,
                startLogDate: null,
                currentBaseDate: new Date(),
                calendarKey: 0
            };
        },
        computed: {
            isCurrentMonthDisplayed() {
                if (!this.currentMonthMin) return true;
                const nowYm = format(new Date(), 'yyyy-MM');
                return this.currentMonthMin.startsWith(nowYm);
            },
            isPrevMonthVisible() {
                if (!this.prevMonthMin || !this.prevMonthMax) return false;
                if (this.startLogDate && this.prevMonthMax < this.startLogDate) {
                    return false;
                }

                let [sy, sm, sd] = this.prevMonthMin.split('-').map(Number);
                let [ey, em, ed] = this.prevMonthMax.split('-').map(Number);
                let cur = new Date(sy, sm - 1, sd);
                const end = new Date(ey, em - 1, ed);

                let hasEnabledDate = false;
                while (cur <= end) {
                    const ymd = format(cur, 'yyyy-MM-dd');
                    if (this.isDateAvailable(ymd, cur)) {
                        hasEnabledDate = true;
                        break;
                    }
                    cur.setDate(cur.getDate() + 1);
                }

                return hasEnabledDate;
            }
        },
        mounted() {
            this.modal = this.$refs['md-this'];
        },
        methods: {
            setupMonthRanges(baseDate = new Date()) {
                const d = baseDate instanceof Date && !isNaN(baseDate) ? baseDate : new Date();

                const prevStart = new Date(d.getFullYear(), d.getMonth() - 1, 1);
                const prevEnd = new Date(d.getFullYear(), d.getMonth(), 0);

                const currentStart = new Date(d.getFullYear(), d.getMonth(), 1);
                const currentEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);

                const nextStart = new Date(d.getFullYear(), d.getMonth() + 1, 1);
                const nextEnd = new Date(d.getFullYear(), d.getMonth() + 2, 0);

                this.prevMonthMin = format(prevStart, 'yyyy-MM-dd');
                this.prevMonthMax = format(prevEnd, 'yyyy-MM-dd');
                this.prevMonthInitial = this.prevMonthMin;

                this.currentMonthMin = format(currentStart, 'yyyy-MM-dd');
                this.currentMonthMax = format(currentEnd, 'yyyy-MM-dd');
                this.currentMonthInitial = this.currentMonthMin;

                this.nextMonthMin = format(nextStart, 'yyyy-MM-dd');
                this.nextMonthMax = format(nextEnd, 'yyyy-MM-dd');
                this.nextMonthInitial = this.nextMonthMin;
            },
            async loadThreeMonthsDataAsync() {
                if (!this.prevMonthMin || !this.nextMonthMax) return;
                const prevYear = parseInt(this.prevMonthMin.slice(0, 4));
                const nextYear = parseInt(this.nextMonthMax.slice(0, 4));

                for (let yr = prevYear; yr <= nextYear; yr++) {
                    await this.loadHolidaysForYearAsync(yr);
                }
                await this.loadBookedDatesRangeAsync(this.prevMonthMin, this.nextMonthMax);
            },
            populateCurrentAbsenceDates(fromStr, toStr) {
                this.currentAbsenceDatesMap = {};
                if (!fromStr || !toStr) return;
                let [sy, sm, sd] = fromStr.split('-').map(Number);
                let [ey, em, ed] = toStr.split('-').map(Number);
                let cur = new Date(sy, sm - 1, sd);
                const end = new Date(ey, em - 1, ed);
                while (cur <= end) {
                    const ymd = format(cur, 'yyyy-MM-dd');
                    this.currentAbsenceDatesMap[ymd] = true;
                    cur.setDate(cur.getDate() + 1);
                }
            },
            isCurrentAbsenceDate(ymd) {
                return !!this.currentAbsenceDatesMap[ymd];
            },
            async applyAbsenceFormAsync(item, types, onApplyCb, initialDate, startLogDate) {
                this.isCorrection = item.isSaved() && !!item.referenceUid;
                this.correctionReferenceUid = item.referenceUid;
                this.pickedAbsenceType = item.workType || "Vacation";
                this.absenceTypes = types;
                this.fromDate = "";
                this.toDate = "";
                this.totalDays = 0;
                this.calendarKey++;
                this.clearAllCalendarInternalSelections();
                this.onAppliedChangesAsync = onApplyCb;
                this.bookedDaysMap = {};
                this.currentAbsenceDatesMap = {};
                this.startLogDate = startLogDate || null;

                const base = new Date();
                this.currentBaseDate = base;
                this.setupMonthRanges(base);

                this.modal.show();

                if (this.isCorrection) {
                    this.loading = true;
                    await this.$http
                        .get('/TimeManagement/LoadAbsence', { params: { referenceUid: item.referenceUid } })
                        .then(async (resp) => {
                            this.fromDate = resp.data.fromDate.split('T')[0];
                            this.toDate = resp.data.toDate.split('T')[0];
                            if (resp.data.type) {
                                this.pickedAbsenceType = resp.data.type;
                            }
                            this.populateCurrentAbsenceDates(this.fromDate, this.toDate);

                            const absenceBase = new Date(this.fromDate);
                            this.currentBaseDate = absenceBase;
                            this.setupMonthRanges(absenceBase);
                            await this.loadThreeMonthsDataAsync();

                            Object.keys(this.currentAbsenceDatesMap).forEach(d => {
                                delete this.bookedDaysMap[d];
                            });

                            this.recalculateEligibleDays();
                            this.loadedPickSignature = this.getCurrentPickSignature();
                        })
                        .finally(this.stopSpinners);
                } else {
                    await this.loadThreeMonthsDataAsync();
                }
            },
            async onModelShown() {
                if (!this.pickedAbsenceType) {
                    this.pickedAbsenceType = "Vacation";
                }
                if (this.absenceTypes.length > 0 && this.startLogDate)
                    return;
                this.loading = true;
                await this.$http
                    .get('/Common/ListMetadata')
                    .then(async (resp) => {
                        this.absenceTypes = resp.data.absenceTypes;
                        if (resp.data.startLogDate) {
                            this.startLogDate = resp.data.startLogDate.split('T')[0];
                        }
                        if (!this.pickedAbsenceType) {
                            this.pickedAbsenceType = "Vacation";
                        }
                    })
                    .finally(this.stopSpinners);
            },
            async loadHolidaysForYearAsync(year) {
                const country = this.$store?.state?.countryCode || 'BG';
                const cacheKey = `${country}_${year}`;
                if (this.publicHolidays[cacheKey]?.length) return;
                try {
                    const resp = await this.$http.get(`/ExternalApi/PublicHolidays/${year}?countryCode=${country}`);
                    this.$set(this.publicHolidays, cacheKey, resp.data || []);
                    this.$set(this.publicHolidays, year, resp.data || []);
                } catch (err) {
                    console.error('Failed to load public holidays for ' + year, err);
                }
            },
            async loadBookedDatesRangeAsync(startStr, endStr) {
                const params = {
                    start: startStr,
                    end: endStr
                };
                if (this.isCorrection && this.correctionReferenceUid) {
                    params.excludeReferenceUid = this.correctionReferenceUid;
                }
                try {
                    const resp = await this.$http.get('/TimeManagement/GetPickedRangeDayStatuses', { params });
                    const statuses = resp.data.calendarDayStatuses || [];
                    statuses.forEach(st => {
                        if (st.style && st.style !== "") {
                            this.$set(this.bookedDaysMap, st.date, true);
                        }
                    });
                } catch (err) {
                    console.error('Failed to load booked dates', err);
                }
            },
            async navigateMonths(delta) {
                const newBase = new Date(this.currentBaseDate.getFullYear(), this.currentBaseDate.getMonth() + delta, 1);
                this.currentBaseDate = newBase;
                this.setupMonthRanges(newBase);
                this.loading = true;
                await this.loadThreeMonthsDataAsync().finally(this.stopSpinners);
            },
            async navigateToday() {
                const base = new Date();
                this.currentBaseDate = base;
                this.setupMonthRanges(base);
                this.loading = true;
                await this.loadThreeMonthsDataAsync().finally(this.stopSpinners);
            },
            isPrevNavDisabled(delta) {
                if (!this.startLogDate) return false;
                const testDate = new Date(this.currentBaseDate.getFullYear(), this.currentBaseDate.getMonth() + delta, 1);
                const nextEnd = new Date(testDate.getFullYear(), testDate.getMonth() + 2, 0);
                const maxStr = format(nextEnd, 'yyyy-MM-dd');
                return maxStr < this.startLogDate;
            },
            isDateAvailable(ymd, date) {
                if (this.startLogDate && ymd < this.startLogDate) return false;
                let d = date;
                if (!d) {
                    let [y, m, day] = ymd.split('-').map(Number);
                    d = new Date(y, m - 1, day);
                }
                const dayOfWeek = d.getDay();
                if (dayOfWeek === 0 || dayOfWeek === 6) return false;
                if (this.isHoliday(ymd)) return false;
                if (this.isCorrection && this.isCurrentAbsenceDate(ymd)) return true;
                if (this.isBooked(ymd)) return false;
                return true;
            },
            clearAllCalendarInternalSelections() {
                const clear = () => {
                    const cals = [this.$refs.prevCal, this.$refs.currCal, this.$refs.nextCal];
                    cals.forEach(cal => {
                        if (cal && cal.selectedYMD) {
                            cal.selectedYMD = '';
                        }
                    });
                };
                clear();
                this.$nextTick(clear);
            },
            onCalendarSelected(ymd) {
                if (!ymd) return;

                if (!this.isDateAvailable(ymd)) {
                    this.clearAllCalendarInternalSelections();
                    return;
                }

                if (!this.fromDate || (this.fromDate && this.toDate)) {
                    // First click: start new selection
                    this.fromDate = ymd;
                    this.toDate = "";
                } else {
                    // Second click: finish range
                    if (ymd >= this.fromDate) {
                        this.toDate = ymd;
                    } else {
                        this.toDate = this.fromDate;
                        this.fromDate = ymd;
                    }
                }
                this.recalculateEligibleDays();
                this.clearAllCalendarInternalSelections();
            },
            resetDateRange() {
                this.fromDate = "";
                this.toDate = "";
                this.totalDays = 0;
                this.calendarKey++;
                this.clearAllCalendarInternalSelections();
            },
            isHoliday(ymd) {
                if (!ymd) return false;
                const year = ymd.slice(0, 4);
                const yearHolidays = this.publicHolidays[year];
                if (!yearHolidays) return false;
                return yearHolidays.some(h => h.date === ymd);
            },
            isBooked(ymd) {
                if (!ymd) return false;
                return !!this.bookedDaysMap[ymd];
            },
            calendarDisabledDates(ymd) {
                if (this.startLogDate && ymd < this.startLogDate) return true;
                return false;
            },
            statusStyle(ymd, date) {
                const isFromOrTo = (this.fromDate && ymd === this.fromDate) || (this.toDate && ymd === this.toDate);
                const inRange = this.fromDate && this.toDate && ymd >= this.fromDate && ymd <= this.toDate;

                if (isFromOrTo) {
                    return 'cal-edge font-weight-bold';
                }
                if (inRange) {
                    const isBeforeStart = this.startLogDate && ymd < this.startLogDate;
                    const day = date.getDay();
                    const isWk = (day === 0 || day === 6);
                    const isHol = this.isHoliday(ymd);
                    const isBkd = (!this.isCorrection || !this.isCurrentAbsenceDate(ymd)) && this.isBooked(ymd);

                    if (isBkd) {
                        return 'cal-booked cal-in-range-skipped';
                    }
                    if (isHol) {
                        return 'cal-holiday cal-in-range-skipped';
                    }
                    if (isWk || isBeforeStart) {
                        return 'cal-weekend cal-in-range-skipped';
                    }
                    return 'cal-eligible font-weight-bold';
                }

                if (this.startLogDate && ymd < this.startLogDate) {
                    return 'cal-disabled';
                }
                if (this.isBooked(ymd) && (!this.isCorrection || !this.isCurrentAbsenceDate(ymd))) {
                    return 'cal-booked';
                }
                if (this.isHoliday(ymd)) {
                    return 'cal-holiday';
                }
                const day = date.getDay();
                if (day === 0 || day === 6) {
                    return 'cal-weekend';
                }
                return 'cal-available';
            },
            recalculateEligibleDays() {
                if (!this.fromDate || !this.toDate) {
                    this.totalDays = 0;
                    return;
                }
                if (this.fromDate > this.toDate) {
                    this.totalDays = 0;
                    return;
                }

                let count = 0;
                let [sy, sm, sd] = this.fromDate.split('-').map(Number);
                let [ey, em, ed] = this.toDate.split('-').map(Number);
                let current = new Date(sy, sm - 1, sd);
                let end = new Date(ey, em - 1, ed);

                while (current <= end) {
                    const day = current.getDay();
                    const isWeekend = (day === 0 || day === 6);
                    const ymd = format(current, "yyyy-MM-dd");
                    const isHol = this.isHoliday(ymd);
                    const isBkd = (!this.isCorrection || !this.isCurrentAbsenceDate(ymd)) && this.isBooked(ymd);
                    const isBeforeStart = this.startLogDate && ymd < this.startLogDate;

                    if (!isWeekend && !isHol && !isBkd && !isBeforeStart) {
                        count++;
                    }
                    current.setDate(current.getDate() + 1);
                }
                this.totalDays = count;
            },
            getCurrentPickSignature() {
                return {
                    type: this.pickedAbsenceType,
                    from: this.fromDate,
                    to: this.toDate,
                    days: this.totalDays
                };
            },
            canConfirmApplyAbsence() {
                if (this.totalDays <= 0 || !this.pickedAbsenceType || !this.fromDate || !this.toDate) {
                    return false;
                }
                if (!this.isCorrection) {
                    return true;
                }
                let pickSig = this.getCurrentPickSignature();
                return this.loadedPickSignature.type !== pickSig.type
                    || this.loadedPickSignature.days !== pickSig.days
                    || this.loadedPickSignature.from !== pickSig.from
                    || this.loadedPickSignature.to !== pickSig.to;
            },
            canShowRemoveButton() {
                if (!this.isCorrection || !this.loadedPickSignature) return false;
                return this.fromDate === this.loadedPickSignature.from && this.toDate === this.loadedPickSignature.to;
            },
            async applyAbsence() {
                if (this.totalDays <= 0) {
                    this.toastWarning('Cannot apply absence with 0 eligible working days.');
                    return;
                }
                const formData = new FormData();
                formData.append('type', this.pickedAbsenceType);
                formData.append('fromDate', this.fromDate);
                formData.append('toDate', this.toDate);
                if (this.isCorrection) {
                    formData.append('originalAbsenceReferenceUid', this.correctionReferenceUid);
                }

                this.loading = true;
                await this.$http
                    .post('/TimeManagement/ApplyAbsence', formData)
                    .then(async () => {
                        this.toastSuccess('Saved!');
                        this.closeWindow();
                        if (typeof this.onAppliedChangesAsync === "function")
                            await this.onAppliedChangesAsync();
                    })
                    .finally(this.stopSpinners);
            },
            async removeAbsence() {
                this.loading = true;
                await this.$http
                    .delete('/TimeManagement/DeleteAbsence', { params: { referenceUid: this.correctionReferenceUid } })
                    .then(async () => {
                        this.toastSuccess('Saved!');
                        this.closeWindow();
                        if (typeof this.onAppliedChangesAsync === "function")
                            await this.onAppliedChangesAsync();
                    })
                    .finally(this.stopSpinners);
            },
            closeWindow() {
                this.modal.hide();
            },
            stopSpinners() {
                this.loading = false;
            }
        }
    };
</script>

<style scoped>
    @media (min-width: 768px) {
        ::v-deep .modal-dialog-apply-absence {
            max-width: min(1160px, 96vw) !important;
            width: 96vw !important;
            margin: 1.5rem auto !important;
        }
    }
    @media (min-width: 1200px) {
        ::v-deep .modal-dialog-apply-absence {
            max-width: 1140px !important;
            width: 100% !important;
        }
    }

    .calendar-container-row {
        gap: 16px;
        max-width: 100%;
    }
    .calendar-wrapper {
        flex: 0 0 310px;
        width: 100%;
        max-width: 310px;
    }
    .absence-modal-calendar {
        margin: 0 auto;
        width: 100%;
        max-width: 310px;
    }
    .calendar-nav-step-btn {
        min-width: 36px;
        font-size: 1.1rem;
        line-height: 1;
    }
    .calendar-nav-today-btn {
        padding: 0.25rem 0.75rem;
        font-weight: 500;
    }
    .absence-action-btn {
        min-width: 80px;
        font-weight: 500;
    }
    /* Hide calendar navigation headers so users pick across the displayed months */
    ::v-deep .absence-modal-calendar .b-calendar-nav {
        display: none !important;
    }
    /* Month Header */
    ::v-deep .absence-modal-calendar .b-calendar-grid-caption {
        font-weight: 700;
        font-size: 1.05rem;
        padding: 0.4rem 0.2rem;
        color: #212529;
    }

    /* Legend */
    .calendar-legend {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 8px 16px;
        margin-top: 6px;
        margin-bottom: 8px;
        padding: 0 8px;
        max-width: 100%;
    }
    .legend-item {
        display: inline-flex;
        align-items: center;
        font-size: 0.8rem;
        color: inherit;
        font-weight: 500;
        white-space: nowrap;
    }
    .legend-dot {
        width: 12px;
        height: 12px;
        border-radius: 2px;
        display: inline-block;
        margin-right: 6px;
    }
    .legend-dot.legend-eligible {
        background-color: #0284c7;
    }
    .legend-dot.legend-booked {
        background-color: #d97706;
        border: 1px solid #b45309;
    }
    .legend-dot.legend-holiday {
        background-color: #0d9488;
    }
    .legend-dot.legend-weekend {
        background-color: #64748b;
    }

    /* Calendar Day Cells */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col {
        transition: background-color 0.15s ease-in-out;
    }

    /* Suppress BootstrapVue native single-date button selection and focus styles */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .btn.btn-primary,
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .btn.active,
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .btn:focus,
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .btn.focus {
        background-color: transparent !important;
        border-color: transparent !important;
        box-shadow: none !important;
        outline: none !important;
    }
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col:not(.cal-edge) .btn.btn-primary,
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col:not(.cal-edge) .btn.active {
        color: inherit !important;
    }

    /* Edge of selection (From and To) */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-edge {
        background-color: #007bff !important;
        border-radius: 3px;
    }
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-edge .btn {
        color: #ffffff !important;
        font-weight: 700 !important;
        background-color: transparent !important;
        box-shadow: none !important;
    }

    /* Eligible working days inside range */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-eligible {
        background-color: #cce5ff !important;
    }
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-eligible .btn {
        color: #004085 !important;
        font-weight: 600 !important;
        background-color: transparent !important;
    }

    /* Already Logged / Booked */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-booked {
        background-color: #fff3cd !important;
        cursor: not-allowed !important;
    }
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-booked .btn {
        color: #856404 !important;
        font-weight: 500 !important;
        background-color: transparent !important;
        cursor: not-allowed !important;
        pointer-events: none;
    }

    /* Public Holiday */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-holiday {
        background-color: #d1ecf1 !important;
        cursor: not-allowed !important;
    }
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-holiday .btn {
        color: #0c5460 !important;
        font-weight: 600 !important;
        background-color: transparent !important;
        cursor: not-allowed !important;
        pointer-events: none;
    }

    /* Weekend / Non-working */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-weekend {
        background-color: #e9ecef !important;
        cursor: not-allowed !important;
    }
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-weekend .btn {
        color: #6c757d !important;
        background-color: transparent !important;
        cursor: not-allowed !important;
        pointer-events: none;
    }

    /* Skipped days inside range */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-in-range-skipped .btn {
        opacity: 0.55;
    }

    /* Disabled / before start log date */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-disabled {
        background-color: #f8f9fa !important;
        cursor: not-allowed !important;
    }
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-disabled .btn {
        color: #adb5bd !important;
        cursor: not-allowed !important;
        pointer-events: none;
    }

    /* Available working days hover effect */
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-available {
        cursor: pointer;
    }
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-available:hover {
        background-color: #e2e6ea;
    }
    ::v-deep .absence-modal-calendar .b-calendar-grid-body .col.cal-available .btn {
        cursor: pointer !important;
    }

</style>
<style>
    /* Dark Mode overrides for absence calendar (unscoped so [data-theme="dark"] on <html> matches) */
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-caption {
        color: var(--vt-text) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-eligible {
        background-color: rgba(2, 132, 199, 0.35) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-eligible .btn {
        color: #7dd3fc !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-booked {
        background-color: rgba(217, 119, 6, 0.3) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-booked .btn {
        color: #fde68a !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-holiday {
        background-color: rgba(13, 148, 136, 0.3) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-holiday .btn {
        color: #5eead4 !important;
    }
    [data-theme="dark"] .legend-dot.legend-weekend {
        background-color: #64748b !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-weekend {
        background-color: rgba(100, 116, 139, 0.22) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-weekend .btn {
        color: #94a3b8 !important;
        opacity: 0.85 !important;
        background-color: transparent !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-disabled {
        background-color: rgba(100, 116, 139, 0.14) !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-disabled .btn {
        color: #64748b !important;
        opacity: 0.75 !important;
        background-color: transparent !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-available:hover {
        background-color: #334155 !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .btn.btn-primary,
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .btn.active,
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .btn:focus,
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .btn.focus {
        background-color: transparent !important;
        border-color: transparent !important;
        box-shadow: none !important;
        outline: none !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col:not(.cal-edge) .btn.btn-primary,
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col:not(.cal-edge) .btn.active {
        color: inherit !important;
    }
    [data-theme="dark"] .absence-modal-calendar .b-calendar-grid-body .col.cal-edge .btn {
        color: #ffffff !important;
        background-color: transparent !important;
        box-shadow: none !important;
    }
</style>
